import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  GetCompletedAndMissedActivitiesCountResponse,
  GetAllCHMetricsResponse,
  GetActivitiesInProgressCountResponse,
} from '@repo/ui';
import { GetAllUsersMetricsResponse } from 'domain/models/responses/dashboard';
import {
  GetAllActivitiesByGroup,
  GetAllActivitiesByPlace,
  GetAllActivitiesByStatus,
  GetAllActivitiesByStudent,
  GetAllActivitiesByTask,
  GetMapActivitiesMarkers,
  GetMapInternshipMarkers,
  GetMapActivityDetails,
} from 'domain/usecases/dashboard';

interface IDashboardState {
  filters: any;
  ch: GetAllCHMetricsResponse;
  users: GetAllUsersMetricsResponse;
  completedAndMissedActivitiesCount: GetCompletedAndMissedActivitiesCountResponse;
  activitiesInProgressCount: GetActivitiesInProgressCountResponse;
  activitiesByTask: GetAllActivitiesByTask.Result;
  activitiesByPlace: GetAllActivitiesByPlace.Result;
  activitiesByGroup: GetAllActivitiesByGroup.Result;
  activitiesByStatus: GetAllActivitiesByStatus.Result;
  activitiesByStudent: GetAllActivitiesByStudent.Result;
  mapActivitiesMarkers: GetMapActivitiesMarkers.Result;
  mapInternshipMarkers: GetMapInternshipMarkers.Result;
  mapActivityDetails: GetMapActivityDetails.Result;
}

interface IPayloadCHMetrics extends PayloadAction<GetAllCHMetricsResponse> {}
interface IPayloadCompletedAndMissedActivitiesCount
  extends PayloadAction<GetCompletedAndMissedActivitiesCountResponse> {}

interface IPayloadActivitiesInProgressCount
  extends PayloadAction<GetActivitiesInProgressCountResponse> {}
interface IPayloadUsersMetrics
  extends PayloadAction<GetAllUsersMetricsResponse> {}
interface IPayloadFilters extends PayloadAction<any> {}
interface IPayloadActivitiesByPlace
  extends PayloadAction<GetAllActivitiesByPlace.Result> {}
interface IPayloadActivitiesByTask
  extends PayloadAction<GetAllActivitiesByTask.Result> {}
interface IPayloadActivitiesByStudent
  extends PayloadAction<GetAllActivitiesByStudent.Result> {}
interface IPayloadActivitiesByGroup
  extends PayloadAction<GetAllActivitiesByGroup.Result> {}
interface IPayloadActivitiesByStatus
  extends PayloadAction<GetAllActivitiesByStatus.Result> {}

interface IPayloadMapActivitiesMarkers
  extends PayloadAction<GetMapActivitiesMarkers.Result> {}
interface IPayloadMapInternshipMarkers
  extends PayloadAction<GetMapInternshipMarkers.Result> {}
interface IPayloadMapActivityDetails
  extends PayloadAction<GetMapActivityDetails.Result> {}

const initialState: IDashboardState = {
  ch: null,
  users: null,
  filters: null,
  completedAndMissedActivitiesCount: null,
  activitiesInProgressCount: null,
  activitiesByTask: null,
  activitiesByGroup: null,
  activitiesByPlace: null,
  activitiesByStatus: null,
  activitiesByStudent: null,
  mapActivitiesMarkers: null,
  mapInternshipMarkers: null,
  mapActivityDetails: null,
};

export const DashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    loadCHMetrics: (state, action: IPayloadCHMetrics) => {
      state.ch = action.payload;
    },
    loadCompletedAndMissedActivitiesCount: (
      state,
      action: IPayloadCompletedAndMissedActivitiesCount,
    ) => {
      state.completedAndMissedActivitiesCount = action.payload;
    },
    loadActivitiesInProgressCount: (
      state,
      action: IPayloadActivitiesInProgressCount,
    ) => {
      state.activitiesInProgressCount = action.payload;
    },
    loadUsersMetrics: (state, action: IPayloadUsersMetrics) => {
      state.users = action.payload;
    },
    loadFilters: (state, action: IPayloadFilters) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = null;
    },
    loadActivitiesByPlace: (state, action: IPayloadActivitiesByPlace) => {
      state.activitiesByPlace = action.payload;
    },
    loadActivitiesByTask: (state, action: IPayloadActivitiesByTask) => {
      state.activitiesByTask = action.payload;
    },
    loadActivitiesByStudent: (state, action: IPayloadActivitiesByStudent) => {
      state.activitiesByStudent = action.payload;
    },
    loadActivitiesByGroup: (state, action: IPayloadActivitiesByGroup) => {
      state.activitiesByGroup = action.payload;
    },
    loadActivitiesByStatus: (state, action: IPayloadActivitiesByStatus) => {
      state.activitiesByStatus = action.payload;
    },
    loadMapActivitiesMarkers: (state, action: IPayloadMapActivitiesMarkers) => {
      state.mapActivitiesMarkers = action.payload;
    },
    loadMapInternshipMarkers: (state, action: IPayloadMapInternshipMarkers) => {
      state.mapInternshipMarkers = action.payload;
    },
    loadMapActivityDetails: (state, action: IPayloadMapActivityDetails) => {
      state.mapActivityDetails = action.payload;
    },
  },
});

export const {
  loadFilters,
  clearFilters,
  loadCHMetrics,
  loadUsersMetrics,
  loadActivitiesByTask,
  loadCompletedAndMissedActivitiesCount,
  loadActivitiesInProgressCount,
  loadActivitiesByGroup,
  loadActivitiesByPlace,
  loadActivitiesByStatus,
  loadActivitiesByStudent,
  loadMapActivitiesMarkers,
  loadMapInternshipMarkers,
  loadMapActivityDetails,
} = DashboardSlice.actions;

export default DashboardSlice.reducer;
